import React, { useCallback, useRef, useState } from 'react';
import { reCaptchaSiteKeySetting, isAF } from '../../lib/instanceSettings';
import { useMutation } from "@apollo/client/react";
import { gql } from '@/lib/generated/gql-codegen';
import { useMessages } from '../common/withMessages';
import { getUserABTestKey } from '../../lib/abTestImpl';
import { useClientId } from '../hooks/useClientId.ts';
import { useLocation } from '../../lib/routeUtil';
import ContentStyles from "../common/ContentStyles";
import ReCaptcha from "../common/ReCaptcha";
import SignupSubscribeToCurated from "./SignupSubscribeToCurated";
import DeferRender from '../common/DeferRender';
import { ErrorLike } from '@apollo/client';
import useCookies from '@/lib/vendor/react-cookie/useCookies.tsx';
import { defineStyles, useStyles } from '../hooks/useStyles.tsx';

const styles = defineStyles('LoginForm', (theme: ThemeType) => ({
  root: {
    wordBreak: "normal",
    padding: 16,
    marginTop: 0,
    marginBottom: 0,
    width: 252
  }, 
  input: {
    font: 'inherit',
    color: 'inherit',
    display: 'block',
    fontSize: '1.2rem',
    marginBottom: 8,
    padding: 8,
    backgroundColor: theme.palette.panelBackground.darken03,
    width: '100%'
  },
  submit: {
    font: 'inherit',
    color: 'inherit',
    background: theme.palette.grey[200],
    display: 'block',
    textTransform: 'uppercase',
    width: '100%',
    height: 32,
    marginTop: 16,
    cursor: 'pointer',
    fontSize: '1rem'
  }, 
  error: {
    padding: 8,
    color: theme.palette.error.main 
  },
  options: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '1rem',
    marginTop: 4,
    padding: 4
  },
  oAuthBlock: {
    display: 'flex',
    justifyContent: 'space-between',
    '&.ea-forum': {
      maxWidth: 400,
      justifyContent: 'space-around',
      padding: '8px 20px',
    }
  },
  oAuthComment: {
    textAlign: 'center',
    fontSize: '0.8em',
    margin: 10
  },
  oAuthLink: {
    color: `${theme.palette.text.slightlyDim2} !important`,
    fontSize: '0.9em',
    padding: 6,
    textTransform: 'uppercase'
  },
  primaryBtn: {
    background: theme.palette.primary.main,
    color: `${theme.palette.buttons.primaryDarkText} !important`,
    fontSize: '0.9em',
    padding: '6px 12px',
    textTransform: 'uppercase',
    borderRadius: 4
  },
  toggle: {
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.link.dim,
    }
  }
}))

type possibleActions = "login" | "signup" | "pwReset"

const currentActionToButtonText: Record<possibleActions, string> = {
  login: "Log In",
  signup: "Sign Up",
  pwReset: "Request Password Reset"
}

const LoginForm = ({ startingState = "login", returnTo }: {
  startingState?: possibleActions,
  returnTo?: string
}) => {
  const classes = useStyles(styles);
  const hasSubscribeToCuratedCheckbox = !isAF();

  const { pathname } = useLocation()
  const reCaptchaToken = useRef<string|null>(null);
  const [username, setUsername] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [email, setEmail] = useState<string>("")
  const { flash } = useMessages();
  const [currentAction, setCurrentAction] = useState<possibleActions>(startingState)
  const [subscribeToCurated, setSubscribeToCurated] = useState<boolean>(hasSubscribeToCuratedCheckbox)
  const [_, setCookie] = useCookies(["loginToken"]);

  const saveLoginToken = useCallback((token: string) => {
    // The graphql request with a "login" or "signup" mutation returns a login
    // token in the graphql response. It will also use a header to set a cookie,
    // but only if using the non-streaming graphql API, so we have to convert
    // the token into a cookie ourselves.
    setCookie("loginToken", token, {
      maxAge: 315360000,
      path: "/",
    });
  }, [setCookie]);


  const [loginMutation] = useMutation(gql(`
    mutation login($username: String, $password: String) {
      login(username: $username, password: $password) {
        token
      }
    }
  `), { errorPolicy: 'all' })

  const [signupMutation] = useMutation(gql(`
    mutation signup($email: String, $username: String, $password: String, $subscribeToCurated: Boolean, $reCaptchaToken: String, $abTestKey: String) {
      signup(email: $email, username: $username, password: $password, subscribeToCurated: $subscribeToCurated, reCaptchaToken: $reCaptchaToken, abTestKey: $abTestKey) {
        token
      }
    }
  `), { errorPolicy: 'all' })

  const [pwResetMutation] = useMutation(gql(`
    mutation resetPassword($email: String) {
      resetPassword(email: $email)
    }
  `), { errorPolicy: 'all' })

  const [displayedError, setDisplayedError] = useState<string|null>(null);
  const clientId = useClientId();

  const showError = (error: ErrorLike) => {
    setDisplayedError(error.message);
  }
  
  const loginSuccess = useCallback(() => {
    if (returnTo) {
      window.location.href = returnTo;
    } else {
      location.reload()
    }
  }, [returnTo])

  const submitFunction = async (e: AnyBecauseTodo) => {
    e.preventDefault();
    const signupAbTestKey = getUserABTestKey({clientId});

    if (currentAction === 'login') {
      const { data, error } = await loginMutation({
        variables: { username, password }
      })
      if (error) {
        showError(error);
      }
      if (data?.login?.token) {
        saveLoginToken(data.login.token);
        loginSuccess();
      }
    } else if (currentAction === 'signup') {
      const { data, error } = await signupMutation({
        variables: {
          email, username, password,
          reCaptchaToken: reCaptchaToken.current,
          abTestKey: signupAbTestKey,
          subscribeToCurated
        }
      })
      if (error) {
        showError(error);
      }
      if (data?.signup?.token) {
        saveLoginToken(data.signup.token);
        loginSuccess();
      }
    } else if (currentAction === 'pwReset') {
      const { data, error } = await pwResetMutation({
        variables: { email }
      })
      if (error) {
        showError(error);
      }
      if (data?.resetPassword) {
        flash(data?.resetPassword)
      }
    }
  }

  const oauthReturnTo = encodeURIComponent(returnTo ?? pathname);

  return <ContentStyles contentType="commentExceptPointerEvents">
    {reCaptchaSiteKeySetting.get() && <DeferRender ssr={false}>
      <ReCaptcha verifyCallback={(token) => reCaptchaToken.current = token} action="login/signup"/>
    </DeferRender>}
    <form className={classes.root} onSubmit={submitFunction}>
      {["signup", "pwReset"].includes(currentAction) && <input value={email} type="text" name="email" placeholder="email" className={classes.input} onChange={event => setEmail(event.target.value)} />}
      {["signup", "login"].includes(currentAction) && <>
        <input
          value={username} type="text" name="username"
          autoComplete="username"
          placeholder={currentAction === "signup" ? "username" : "username or email"}
          className={classes.input}
          onChange={event => setUsername(event.target.value)}
        />
        <input
          value={password} type="password" name="password"
          autoComplete={currentAction==="signup" ? "new-password" : "current-password"}
          placeholder={(currentAction==="signup") ? "create password" : "password"}
          className={classes.input}
          onChange={event => setPassword(event.target.value)}
        />
      </>}
      <input type="submit" className={classes.submit} value={currentActionToButtonText[currentAction]} />
      
      {currentAction === "signup" && hasSubscribeToCuratedCheckbox &&
        <SignupSubscribeToCurated defaultValue={subscribeToCurated} onChange={(checked: boolean) => setSubscribeToCurated(checked)} />
      }
      <div className={classes.options}>
        {currentAction !== "login" && <span className={classes.toggle} onClick={() => setCurrentAction("login")}> Log In </span>}
        {currentAction !== "signup" && <span className={classes.toggle} onClick={() => setCurrentAction("signup")}> Sign Up </span>}
        {currentAction !== "pwReset" && <span className={classes.toggle} onClick={() => setCurrentAction("pwReset")}> Reset Password </span>}
      </div>
      <>
        <div className={classes.oAuthComment}>...or continue with</div>
        <div className={classes.oAuthBlock}>
          <a className={classes.oAuthLink} href={`/auth/google?returnTo=${oauthReturnTo}`}>GOOGLE</a>
          <a className={classes.oAuthLink} href={`/auth/github?returnTo=${oauthReturnTo}`}>GITHUB</a>
        </div>
      </>
      {displayedError && <div className={classes.error}>{displayedError}</div>}
    </form>
  </ContentStyles>;
}

export default LoginForm;


